import { createFileRoute, notFound } from "@tanstack/react-router";

import { sdk } from "@/api/sdk";
import { useLoadInBrowser } from "@/features/blog/hooks/use-load-in-browser";
import { HelpArticleHero, HelpArticleView } from "@/features/help/modules/help-article/help-article";
import { PortalHead, PortalPage } from "@/features/portal/components/portal-chrome";
import { helpArticleHead } from "@/features/help/utils/help-head";
import { reportApiOutage } from "@/lib/api-outage";
import { m } from "@/paraglide/messages.js";
import { HelpAudience } from "@complete-athlete/enums";

/**
 * One help article, rendered on the server. Its address is the same in every language (ADR 0027),
 * so the root's canonical and alternates fit it as they are.
 */
export const Route = createFileRoute("/(public)/help/$slug")({
  loader: async ({ params }) => {
    const { data, status } = await sdk.help.articles.$slug.$get
      .setParams({ slug: params.slug })
      .setQueryParams({ audience: HelpAudience.ATHLETE })
      .send();
    if (status === 404) throw notFound();
    // On the server a failed request hands the page to the browser, which asks again.
    if (!data) reportApiOutage();
    if (!data && typeof window !== "undefined") throw new Error(`Could not load this article (status ${status ?? "none"})`);
    return { article: data ?? null, crumb: data?.title ?? m.help_title() };
  },
  head: ({ loaderData }) => (loaderData?.article ? helpArticleHead(loaderData.article) : {}),
  component: HelpArticleRoute,
});

function HelpArticleRoute() {
  const { article } = Route.useLoaderData();
  useLoadInBrowser(article);

  return (
    <PortalPage head={<PortalHead>{article ? <HelpArticleHero article={article} /> : null}</PortalHead>}>
      {article ? <HelpArticleView key={article.slug} article={article} /> : null}
    </PortalPage>
  );
}
